import React, { useState } from 'react';
import { User, Camera, Save, ScanSearch } from 'lucide-react';
import ImageAdjustModal from '../ImageAdjustModal';
import Avatar from '../Avatar';

const COLORS = [
    'oklch(0.72 0.155 48)',  // apricot
    'oklch(0.74 0.165 150)', // spring green
    'oklch(0.68 0.145 242)', // cobalt
    'oklch(0.75 0.15 95)',   // gold
    'oklch(0.72 0.16 190)',  // teal
    'oklch(0.72 0.15 300)',  // violet
    'oklch(0.72 0.16 340)',  // magenta
    'oklch(0.74 0.14 70)',   // amber
];

const UserSection = ({ userData, onUpdate, onImageUpload, roommates = [] }) => {
    const takenColors = roommates.map(r => r.color).filter(Boolean);
    const availableColors = COLORS.filter(c => !takenColors.includes(c));

    const [name, setName] = useState(userData?.name || '');
    const [color, setColor] = useState(userData?.color || availableColors[0] || COLORS[0]);
    const [avatarUrl, setAvatarUrl] = useState(userData?.avatarUrl || '');
    const [avatarPosition, setAvatarPosition] = useState(userData?.avatarPosition || '50% 50%');
    const [avatarZoom, setAvatarZoom] = useState(userData?.avatarZoom || 1);
    const [isEditing, setIsEditing] = useState(false);
    const [uploading, setUploading] = useState(false);

    // State for the adjust modal
    const [adjustModalUrl, setAdjustModalUrl] = useState(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const uploadedUrl = await onImageUpload(file);
            // Open adjust modal with the fresh URL
            setAdjustModalUrl(uploadedUrl);
        } catch (err) {
            alert(err.response?.data?.error || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    // Called when the user saves positioning in the modal
    const handleAdjustSave = (position, zoom) => {
        setAvatarUrl(adjustModalUrl);
        setAvatarPosition(position);
        setAvatarZoom(zoom);
        setAdjustModalUrl(null);
    };

    // Filter taken colors except the one the user currently has
    const otherTakenColors = takenColors.filter(c => c !== userData?.color);

    const handleSubmit = () => {
        onUpdate({ name, color, avatarUrl, avatarPosition, avatarZoom });
        setIsEditing(false);
    };

    return (
        <div className="p-5 bg-ink-card rounded-2xl shadow-sm border border-white/10 mb-6 relative overflow-hidden">
            {/* Background Accent */}
            <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: userData?.color || 'rgba(245,242,236,.15)' }} />

            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xs font-mono font-medium text-ink-fg/40 uppercase tracking-wider">My Account</h3>
                {!isEditing ? (
                    <button onClick={() => setIsEditing(true)} className="text-[10px] font-bold text-mint hover:text-ink-fg uppercase tracking-tight">Edit Profile</button>
                ) : (
                    <button onClick={() => setIsEditing(false)} className="text-[10px] font-bold text-ink-fg/40 hover:text-ink-fg/70 uppercase tracking-tight">Cancel</button>
                )}
            </div>

            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-[10px] font-mono font-medium text-ink-fg/40 uppercase mb-1">Name</label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-mint outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-mono font-medium text-ink-fg/40 uppercase mb-1.5">Personal Color</label>
                        <div className="flex flex-wrap gap-2">
                            {COLORS.map(c => {
                                const isTaken = otherTakenColors.includes(c);
                                return (
                                    <button
                                        key={c}
                                        disabled={isTaken}
                                        onClick={() => !isTaken && setColor(c)}
                                        className={`w-8 h-8 rounded-full border-2 transition-all ${isTaken ? 'opacity-10 scale-50 cursor-not-allowed' : 'active:scale-90 hover:scale-105'} ${color === c ? 'border-ink-fg scale-110' : 'border-white/20 shadow-sm'}`}
                                        style={{ backgroundColor: c }}
                                        title={isTaken ? "Taken by roommate" : ""}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-mono font-medium text-ink-fg/40 uppercase mb-2">Avatar Photo</label>

                        {/* Preview + Adjust button if we already have an avatar */}
                        {avatarUrl && (
                            <div className="flex items-center gap-3 mb-3 p-3 bg-ink-card2 rounded-xl border border-white/10">
                                <Avatar
                                    user={{ avatarUrl, avatarPosition, avatarZoom, name: userData?.name }}
                                    size="w-12 h-12"
                                    textSize="text-sm"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-ink-fg/70 truncate">Avatar set</p>
                                    <p className="text-[10px] text-ink-fg/40">Zoom: {Math.round((avatarZoom || 1) * 100)}%</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setAdjustModalUrl(avatarUrl)}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-mint/10 text-mint text-xs font-bold rounded-lg hover:bg-mint/20 transition-colors border border-mint/20"
                                >
                                    <ScanSearch size={14} /> Adjust
                                </button>
                            </div>
                        )}

                        <div className="flex gap-2">
                            <input
                                value={avatarUrl}
                                onChange={(e) => setAvatarUrl(e.target.value)}
                                placeholder="https://image-link.com/photo.jpg"
                                className="flex-1 bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-mint outline-none transition-all placeholder:text-ink-fg/30"
                            />
                            <label className={`cursor-pointer flex items-center justify-center p-2 rounded-xl border border-white/10 bg-ink-card2 hover:bg-ink-card3 transition-colors ${uploading ? 'animate-pulse opacity-50' : ''}`}>
                                <Camera size={18} className="text-ink-fg/60" />
                                <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} disabled={uploading} />
                            </label>
                        </div>
                        <p className="text-[10px] text-ink-fg/40 mt-1 italic">Paste a URL or upload a file • then click Adjust to crop</p>
                    </div>
                    <button
                        onClick={handleSubmit}
                        className="w-full bg-mint text-ink-shell font-bold py-2.5 rounded-xl hover:brightness-95 transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
                    >
                        <Save size={16} /> Save Changes
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                <div className="flex items-center gap-4">
                    {/* Clickable avatar to open adjust modal (even outside edit mode) */}
                    <div
                        className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 shadow-md relative cursor-pointer group"
                        onClick={() => { if (userData?.avatarUrl) { setIsEditing(true); setAdjustModalUrl(userData.avatarUrl); } }}
                        title={userData?.avatarUrl ? "Click to adjust" : ""}
                    >
                        {userData?.avatarUrl ? (
                            <img
                                src={userData.avatarUrl}
                                alt="Avatar"
                                className="w-full h-full object-cover border-2 border-white/20"
                                style={{
                                    objectPosition: userData?.avatarPosition || '50% 50%',
                                    transform: `scale(${userData?.avatarZoom || 1})`,
                                    transformOrigin: userData?.avatarPosition || '50% 50%',
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div
                            className="w-full h-full items-center justify-center text-ink-shell font-serif text-2xl"
                            style={{
                                backgroundColor: userData?.color || 'oklch(0.72 0.155 48)',
                                display: userData?.avatarUrl ? 'none' : 'flex'
                            }}
                        >
                            {userData?.name?.[0] || <User size={32} />}
                        </div>
                        {userData?.avatarUrl && (
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                <ScanSearch size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        )}
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-ink-card" style={{ backgroundColor: userData?.color || 'rgba(245,242,236,.3)' }} />
                    </div>
                    <div className="min-w-0">
                        <p className="font-serif text-ink-fg text-lg truncate">{userData?.name || "No name"}</p>
                        <p className="text-sm text-ink-fg/50 font-mono truncate">{userData?.email || "No email"}</p>
                    </div>
                </div>

                <div>
                    <p className="text-[10px] font-mono font-medium text-ink-fg/30 uppercase tracking-widest mb-2">Your Color</p>
                    <div className="flex flex-wrap gap-2">
                        {COLORS.map(c => {
                            const owner = roommates.find(r => r.color === c);
                            const isMine = c === userData?.color;
                            return (
                                <div
                                    key={c}
                                    className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-ink-shell ${isMine ? 'ring-2 ring-ink-fg ring-offset-2 ring-offset-ink-card' : ''}`}
                                    style={{ backgroundColor: c }}
                                    title={owner ? owner.name : (isMine ? userData?.name : '')}
                                >
                                    {owner ? owner.name?.[0] : ''}
                                </div>
                            );
                        })}
                    </div>
                </div>
                </div>
            )}

            {/* Adjust Modal */}
            {adjustModalUrl && (
                <ImageAdjustModal
                    imageUrl={adjustModalUrl}
                    initialPosition={avatarPosition}
                    initialZoom={avatarZoom}
                    isCircle={true}
                    onSave={handleAdjustSave}
                    onClose={() => setAdjustModalUrl(null)}
                />
            )}
        </div>
    );
};

export default UserSection;
